import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import './App.css';
import './responsive-utilities.css';
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import TopAd from './components/TopAd';
import RightAd from './components/RightAd';
import BottomAd from './components/BottomAd';
import SecondPage from './components/SecondPage';
import Blog from './components/Blog';
import Agroecology from './components/Agroecology';
import EnglishBlog from './components/EnglishBlog';
import BookStore from './components/BookStore';
import ProfileStory from './components/ProfileStory';
import Gallery from './components/Gallery';
import Services from './components/Services';
import GalleryPage from './pages/GalleryPage';
import FAQPage from './components/FAQPage';
import ContactPage from './components/ContactPage';
import MlBlogPage from './pages/MlBlogPage';
import PostDetailPage from './pages/PostDetailPage';
import EnBlogPage from './pages/EnBlogPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import CartPage from './pages/CartPage';
import PracticalDetailPage from './pages/PracticalDetailPage';
import { apiGet, apiPost } from './api';

function App() {
  const [language, setLanguage] = useState('en');
  const [route, setRoute] = useState(window.location.hash || '#');
  const [cart, setCart] = useState([]);
  const [showCartIcon, setShowCartIcon] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [activeBookImage, setActiveBookImage] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ name: '', content: '' });
  const [hasTopAd, setHasTopAd] = useState(true);
  const [toast, setToast] = useState({ show: false, text: '' });

  const showToast = (text, ms = 1500) => {
    setToast({ show: true, text });
    window.clearTimeout((showToast)._timer);
    (showToast)._timer = window.setTimeout(() => setToast({ show: false, text: '' }), ms);
  };

  const toggleLanguage = () => setLanguage((l) => (l === 'en' ? 'ml' : 'en'));

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash || '#';
      setRoute(hash);
      // Only force-scroll to top for route changes (e.g., #/blog/..., #/services/...).
      // For in-page home sections (hash === '#'), do not alter scroll so custom smooth scroll can run.
      if (hash.startsWith('#/')) {
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Show cart icon only when Book Store is on screen
  useEffect(() => {
    const el = document.getElementById('book-store');
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setShowCartIcon(entry.isIntersecting);
    }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, [route]);

  const cartCount = cart.reduce((n, it) => n + it.quantity, 0);
  const goToCart = () => { window.location.hash = '#/cart'; };
  const addToCart = (book, qty = 1) => {
    setCart((prev) => {
      const ex = prev.find((p) => p.book.id === book.id);
      if (ex) return prev.map((p) => (p.book.id === book.id ? { ...p, quantity: p.quantity + qty } : p));
      return [...prev, { book, quantity: qty }];
    });
    showToast('Book added to the cart successfully');
    window.setTimeout(() => goToCart(), 900);
  };
  const preorder = (book) => {
    setCart([{ book, quantity: 1 }]);
    showToast('Book added to the cart successfully');
    window.setTimeout(() => goToCart(), 900);
  };

  const openBookDetails = async (book) => {
    // Optimistically show current book, then try to fetch full details by slug
    setSelectedBook(book);
    setActiveBookImage(0);
    setComments([]);
    setNewComment({ name: '', content: '' });
    try {
      if (book.slug) {
        const detail = await apiGet(`/api/books/${encodeURIComponent(book.slug)}`, { locale: language });
        if (detail && detail.id) {
          setSelectedBook(detail);
          const list = await apiGet(`/api/books/${detail.id}/comments`);
          setComments(Array.isArray(list) ? list : []);
          return;
        }
      }
      const list = await apiGet(`/api/books/${book.id}/comments`);
      setComments(Array.isArray(list) ? list : []);
    } catch (_) {}
  };
  const closeBookDetails = () => setSelectedBook(null);
  const postComment = async () => {
    if (!selectedBook) return;
    const body = { name: newComment.name, content: newComment.content };
    if (!body.name || !body.content) return;
    try {
      await apiPost(`/api/books/${selectedBook.id}/comments`, body);
      setNewComment({ name: '', content: '' });
      const list = await apiGet(`/api/books/${selectedBook.id}/comments`);
      setComments(Array.isArray(list) ? list : []);
    } catch (_) { alert('Failed to post comment'); }
  };

  return (
    <div className="App">
      {/* Top ad banner fixed at top (20vh) */}
      <TopAd language={language} onAdStatusChange={setHasTopAd} />

      {/* Header fixed below top ad */}
      <Header language={language} toggleLanguage={toggleLanguage} showCartIcon={showCartIcon} cartCount={cartCount} onCartClick={goToCart} />

      {/* Main area laid out with right ad consuming 20% width and 60vh height */}
      <main className={`main-layout ${!hasTopAd ? 'no-top-ad' : ''} ${(route.startsWith('#/blog/ml/') || route.startsWith('#/blog/en/')) ? 'blog-post-page' : ''}`}>
        <div className="main-content">
          {route === '#/cart' ? (
            <CartPage cart={cart} setCart={setCart} onBack={() => { window.location.hash = '#'; }} onOrderPlaced={() => showToast('Order placed successfully')} />
          ) : route === '#/blog/ml' ? (
            <MlBlogPage />
          ) : route.startsWith('#/blog/ml/') ? (
            <PostDetailPage slug={decodeURIComponent(route.replace('#/blog/ml/', ''))} locale="ml" />
          ) : route === '#/blog/en' ? (
            <EnBlogPage />
          ) : route.startsWith('#/blog/en/') ? (
            <PostDetailPage slug={decodeURIComponent(route.replace('#/blog/en/', ''))} locale="en" />
          ) : route.startsWith('#/services/en/') ? (
            <ServiceDetailPage slug={decodeURIComponent(route.replace('#/services/en/', ''))} locale="en" />
          ) : route.startsWith('#/services/ml/') ? (
            <ServiceDetailPage slug={decodeURIComponent(route.replace('#/services/ml/', ''))} locale="ml" />
          ) : route === '#/gallery' ? (
            <GalleryPage language={language} />
          ) : route.startsWith('#/practical/') ? (
            <PracticalDetailPage id={decodeURIComponent(route.replace('#/practical/', ''))} />
          ) : (
            <>
              <Hero language={language} />
              <SecondPage />
              <Blog language="ml" />
              <Agroecology />
              <EnglishBlog />
              <BookStore language={language}
                onAddToCart={addToCart}
                onPreorder={preorder}
                onOpenBook={openBookDetails}
              />
              <ProfileStory />
              <Gallery language={language} />
              <Services language={language} />
              <FAQPage />
              <ContactPage />
            </>
          )}
        </div>
        <div className="right-rail">
          <RightAd language={language} />
        </div>
      </main>

      {/* Bottom ad full-width 20vh */}
      <BottomAd language={language} />

      <Footer language={language} />

      {/* Book details modal */}
      {selectedBook && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '22vh 16px 16px', overflow: 'auto' }}>
          <div style={{ width: 'min(760px, 95vw)', overflow: 'visible', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111827' }}>{selectedBook.name}</h2>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {(selectedBook.status === 'PREBOOK' || selectedBook.isPreorder) && (
                    <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 4 }}><Icon icon="mdi:package-variant" width="16" /> Prebook</span>
                  )}
                  {(selectedBook.status === 'INSTOCK' && (Number(selectedBook.stock || 0) > 0)) && (
                    <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 4 }}><Icon icon="mdi:check-circle" width="16" /> In Stock{selectedBook.stock ? ` (${selectedBook.stock})` : ''}</span>
                  )}
                  {(selectedBook.status === 'SOLD_OUT' || (selectedBook.status === 'INSTOCK' && Number(selectedBook.stock || 0) <= 0)) && (
                    <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 4 }}><Icon icon="mdi:close-circle" width="16" /> Sold Out</span>
                  )}
                </div>
              </div>
              <button onClick={closeBookDetails} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 28, color: '#9ca3af', lineHeight: 1, padding: 0, marginLeft: 16 }} aria-label="Close">×</button>
            </div>
            {/* Content */}
            <div style={{ padding: '24px', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {/* Gallery */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(() => {
                  const gallery = [selectedBook.coverUrl || selectedBook.cover, selectedBook.page1Url, selectedBook.page2Url].filter(Boolean);
                  const current = gallery[activeBookImage] || gallery[0];
                  return (
                    <>
                      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <img src={current} alt={selectedBook.name} style={{ width: 220, height: 300, objectFit: 'cover', display: 'block' }} />
                      </div>
                      {gallery.length > 1 && (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          {gallery.map((url, idx) => (
                            <div key={idx} onClick={() => setActiveBookImage(idx)}
                              style={{ width: 64, height: 86, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: idx === activeBookImage ? '3px solid #2d5016' : '3px solid transparent', transition: 'all 0.2s', boxShadow: idx === activeBookImage ? '0 2px 8px rgba(45,80,22,0.3)' : 'none' }}>
                              <img src={url} alt={`Preview ${idx+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* Details */}
              <div style={{ flex: 1, minWidth: 280 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#2d5016', marginBottom: 12 }}>{selectedBook.price || selectedBook.rate}</div>
                {selectedBook.quality && <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 12, padding: '6px 12px', background: '#f9fafb', borderRadius: 8, display: 'inline-block' }}>Quality: {selectedBook.quality}</div>}
                {selectedBook.description && <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.6, color: '#374151' }}>{selectedBook.description}</p>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
                  {(selectedBook.status === 'PREBOOK' || selectedBook.isPreorder) && (
                    <button onClick={() => preorder(selectedBook)} style={{ padding: '12px 24px', fontSize: 15, fontWeight: 600, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8 }} onMouseOver={(e) => e.currentTarget.style.background = '#d97706'} onMouseOut={(e) => e.currentTarget.style.background = '#f59e0b'}><Icon icon="mdi:package-variant" width="20" /> Preorder Now</button>
                  )}
                  {(selectedBook.status === 'INSTOCK' && Number(selectedBook.stock || 0) > 0) && (
                    <button onClick={() => addToCart(selectedBook)} style={{ padding: '12px 24px', fontSize: 15, fontWeight: 600, background: '#2d5016', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8 }} onMouseOver={(e) => e.currentTarget.style.background = '#1f3a0f'} onMouseOut={(e) => e.currentTarget.style.background = '#2d5016'}><Icon icon="mdi:cart" width="20" /> Add to Cart</button>
                  )}
                  {(selectedBook.status === 'SOLD_OUT' || (selectedBook.status === 'INSTOCK' && Number(selectedBook.stock || 0) <= 0)) && (
                    <button disabled style={{ padding: '12px 24px', fontSize: 15, fontWeight: 600, background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 10, cursor: 'not-allowed' }}>Sold Out</button>
                  )}
                </div>
              </div>
            </div>
            {/* Comments Section */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}><Icon icon="mdi:comment-multiple" width="22" /> Comments</h3>
              {/* Comment Input */}
              <div style={{ padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', background: '#f9fafb', marginBottom: 16 }}>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ position: 'relative' }}>
                    <Icon icon="mdi:account" width="18" style={{ position: 'absolute', left: 12, top: 14, color: '#9ca3af', pointerEvents: 'none' }} />
                    <input
                      placeholder="Your name"
                      value={newComment.name}
                      onChange={(e) => setNewComment({ ...newComment, name: e.target.value })}
                      style={{ padding: '12px 16px 12px 38px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none', transition: 'border 0.2s', width: '100%' }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#2d5016'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                  </div>
                  <div style={{ position: 'relative' }}>
                    <Icon icon="mdi:pencil" width="18" style={{ position: 'absolute', left: 12, top: 14, color: '#9ca3af', pointerEvents: 'none' }} />
                    <textarea
                      placeholder="Share your thoughts about this book..."
                      rows={3}
                      maxLength={1000}
                      value={newComment.content}
                      onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                      style={{ padding: '12px 16px 12px 38px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none', resize: 'vertical', fontFamily: 'inherit', transition: 'border 0.2s', width: '100%' }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#2d5016'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={postComment}
                      disabled={!newComment.name.trim() || !newComment.content.trim()}
                      style={{ padding: '10px 20px', fontSize: 14, fontWeight: 600, background: (!newComment.name.trim() || !newComment.content.trim()) ? '#d1d5db' : '#2d5016', color: '#fff', border: 'none', borderRadius: 8, cursor: (!newComment.name.trim() || !newComment.content.trim()) ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                      onMouseOver={(e) => { if (newComment.name.trim() && newComment.content.trim()) e.currentTarget.style.background = '#1f3a0f'; }}
                      onMouseOut={(e) => { if (newComment.name.trim() && newComment.content.trim()) e.currentTarget.style.background = '#2d5016'; }}
                    >
                      Post Comment
                    </button>
                  </div>
                </div>
              </div>
              {/* Comment List */}
              <div style={{ display: 'grid', gap: 12 }}>
                {comments.map((c) => (
                  <div key={c.id} style={{ padding: 16, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb', display: 'flex', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #2d5016, #4a7c2a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{c.name.charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 4 }}>{c.name}</div>
                      <div style={{ fontSize: 14, lineHeight: 1.5, color: '#4b5563' }}>{c.content}</div>
                      {c.createdAt && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>{new Date(c.createdAt).toLocaleDateString()}</div>}
                    </div>
                  </div>
                ))}
                {comments.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 14 }}>No comments yet. Be the first to share your thoughts!</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast popup card */}
      {toast.show && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, pointerEvents: 'none' }}>
          <div style={{ background: '#ffffff', color: '#111827', padding: '14px 18px', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, pointerEvents: 'auto' }}>
            <span style={{ display: 'inline-flex', width: 22, height: 22, alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: '#d1fae5', color: '#065f46' }}>
              <Icon icon="mdi:check-bold" width="14" />
            </span>
            <span>{toast.text}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
